/**
 * ChatInput - Dark theme input with source count indicator
 *
 * Features:
 * - Glass morphism floating bar
 * - Source count indicator
 * - Send button with arrow icon
 */

import { useState, KeyboardEvent } from 'react';

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// === STYLES ===
const styles = `
  .chat-input-wrapper {
    padding: 16px;
    background: #0A0A0C;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    flex-shrink: 0;
  }

  .chat-input-container {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    padding: 10px 12px 10px 16px;
    background: #1a1a1f;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    transition: all 0.15s;
  }

  .chat-input-container:focus-within {
    border-color: rgba(99, 102, 241, 0.4);
    background: #222228;
  }

  .chat-textarea {
    flex: 1;
    padding: 6px 0;
    background: transparent;
    border: none;
    color: #fff;
    font-size: 14px;
    line-height: 1.5;
    resize: none;
    outline: none;
    font-family: inherit;
    max-height: 100px;
    min-height: 22px;
  }

  .chat-textarea::placeholder {
    color: rgba(100, 116, 139, 0.5);
  }

  .chat-textarea:disabled {
    color: rgba(148, 163, 184, 0.5);
    cursor: not-allowed;
  }

  .input-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .source-count {
    font-size: 11px;
    color: rgba(100, 116, 139, 0.6);
    white-space: nowrap;
    padding: 4px 8px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 6px;
  }

  .send-button {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #6366f1;
    border: none;
    border-radius: 8px;
    color: #fff;
    cursor: pointer;
    transition: all 0.15s;
    flex-shrink: 0;
  }

  .send-button:hover:not(:disabled) {
    background: #818cf8;
  }

  .send-button:disabled {
    background: rgba(99, 102, 241, 0.3);
    color: rgba(255, 255, 255, 0.4);
    cursor: not-allowed;
  }
`;

// === COMPONENT ===
interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  /** Number of selected sources to display */
  sourceCount?: number;
}

export function ChatInput({
  onSend,
  isLoading = false,
  placeholder,
  sourceCount = 0,
}: ChatInputProps) {
  const [input, setInput] = useState('');

  const handleSubmit = () => {
    if (input.trim() && !isLoading) {
      onSend(input.trim());
      setInput('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="chat-input-wrapper">
      <div className="chat-input-container">
        <textarea
          className="chat-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Type a message...'}
          disabled={isLoading}
          rows={1}
        />
        <div className="input-actions">
          {sourceCount > 0 && (
            <span className="source-count">
              {sourceCount} Sources
            </span>
          )}
          <button
            type="button"
            className="send-button"
            onClick={handleSubmit}
            disabled={!input.trim() || isLoading}
            title="Send message"
          >
            <SendIcon />
          </button>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
}
